import React, { useState, useEffect } from "react";
import KpiCard from "../components/Dashboard/KpiCard";
import LineChart from "../components/Charts/LineChart";
import BarChart from "../components/Charts/BarChart";
import TopDistributorsList from "../components/Dashboard/TopDistributorsList";
import ReusableTable from "../components/Table/ReusableTable";
import { TrendingUp, DollarSign, Users, Briefcase, UserMinus } from "lucide-react";
import { toast } from "react-toastify";
import { get, post } from "../utils/api";


// ── Sample Data ────────────────────────────────────────────────────────────

const PRODUCT_SALES_DATA = {
    labels: ["HF CAT II", "HF CAT III", "PMS"],
    datasets: [
        {
            label: "Sales",
            data: [125, 67, 48],
            backgroundColor: ["#6366F1", "#F59E0B", "#10B981"],
        },
    ],
};

const COMMISSION_DATA = {
    labels: ["Upfront Fee", "Placement Fee", "Trail Fee"],
    datasets: [
        {
            label: "Amount",
            data: [45000, 38000, 17000],
            backgroundColor: ["#10B981", "#F59E0B", "#6366F1"],
        },
    ],
};

const TOP_DISTRIBUTORS = [
    { name: "VIJAY WEALTH INDIA PVT LTD", subtitle: "32 sales", amount: "₹1766", status: "Commited", statusColor: "#6366F1" },
    { name: "Finclear Advisors", subtitle: "28 sales", amount: "₹986", status: "Commited", statusColor: "#6366F1" },
    { name: "Influx Investship & Group", subtitle: "21 sales", amount: "₹786", status: "Commited", statusColor: "#6366F1" },
    { name: "Shakar Capital", subtitle: "21 sales", amount: "₹168", status: "Commited", statusColor: "#6366F1" },
];


// ── Component ───────────────────────────────────────────────────────────────
const SalesOverviewDashboard = () => {
    const userDetails = JSON.parse(localStorage.getItem("user_details") || "{}");

    // Dynamic State for KPIs
    const [kpiData, setKpiData] = useState({
        distributors: { value: "...", subtitle: "Loading...", trend: 0 },
        kyc: { value: "...", subtitle: "Loading...", trend: 0 }
    });

    // Dynamic State for Product Sales Table
    const [productSalesData, setProductSalesData] = useState([]);

    // Dynamic State for Top Distributors
    const [topDistributors, setTopDistributors] = useState([]);

    // Dynamic State for Commission Breakdown
    const [commissionData, setCommissionData] = useState({
        upfront: 0,
        placement: 0,
        trail: 0,
        total: 0
    });

    // Dynamic State for Fund and Distributor Mapping
    const [distributorFundDetails, setDistributorFundDetails] = useState([]);

    const [invoiceStatus, setInvoiceStatus] = useState({
        pending: 0,
        approved: 0,
        draft: 0
    });

    const [lastUpdated, setLastUpdated] = useState("...");

    const formatTimestamp = () => {
        const now = new Date();
        const formattedDate = now.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
        const formattedTime = now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
        return `${formattedDate} – ${formattedTime}`;
    };

    const getPreviousMonthYear = () => {
        const d = new Date();
        d.setMonth(d.getMonth() - 1);
        return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    };

    const monthInfo = getPreviousMonthYear();

    // Dynamic State for WIP Activity Table
    const [wipActivityData, setWipActivityData] = useState([]);

    useEffect(() => {
        let isMounted = true;
        setLastUpdated(formatTimestamp());

        const fetchDashboardData = async () => {
            try {
                const [distributorRes, kycRes, prodSalesRes, commRes, topDistRes, wipActivityRes, distributorFundRes, invoiceStatusRes] = await Promise.all([
                    get("/admindashboard/GetTotalDistributor_Details_Count"),
                    get("/admindashboard/Get_KYC_Pending_Count"),
                    post("/admindashboard/Get_MonthLevel_ProductSales_Count", {}),
                    post("/admindashboard/Get_BreakDown_Commission_Total", {}),
                    post("/admindashboard/Get_Top5_TotalCommission_MonthWise", {}),
                    get("/admindashboard/Get_Display_WIP_Distributor_Details_Last_Activity"),
                    get("/admindashboard/GetDistributorFundDetails"),
                    get("/admindashboard/Get_Invoice_Status_Count")
                ]);

                if (!isMounted) return;

                // 0. Distributor Fund Details Mapping
                if (distributorFundRes.data.Success && Array.isArray(distributorFundRes.data.Data)) {
                    setDistributorFundDetails(distributorFundRes.data.Data);
                }

                // 1. Total Distributor Count
                let totalDistributors = 0;
                if (distributorRes.data.Success && Array.isArray(distributorRes.data.Data) && distributorRes.data.Data.length > 0) {
                    totalDistributors = distributorRes.data.Data[0].total_distributor_details_count || 0;
                }

                // 3. KYC Pending Count
                let totalKyc = 0;
                if (kycRes.data.Success && Array.isArray(kycRes.data.Data) && kycRes.data.Data.length > 0) {
                    totalKyc = kycRes.data.Data[0].pending_count || 0;
                }

                // 8. Invoice Status Counts
                if (invoiceStatusRes.data.Success && Array.isArray(invoiceStatusRes.data.Data) && invoiceStatusRes.data.Data.length > 0) {
                    const statusData = invoiceStatusRes.data.Data[0];
                    setInvoiceStatus({
                        pending: parseInt(statusData.PENDING_FOR_APPROVAL || 0, 10),
                        approved: parseInt(statusData.APPROVED || 0, 10),
                        draft: parseInt(statusData.DRAFT || 0, 10)
                    });
                }

                setKpiData({
                    distributors: { value: totalDistributors, subtitle: "Approved and Signed", trend: 0 },
                    kyc: { value: totalKyc, subtitle: "KYC pending prospects", trend: 0 }
                });

                // 4. Product Sales Grid Mapping
                if (prodSalesRes.data.Success && Array.isArray(prodSalesRes.data.Data)) {
                    setProductSalesData(prodSalesRes.data.Data);
                }

                // 5. Commission Breakdown Mapping
                let upfront = 0, placement = 0, trail = 0;
                if (commRes.data.Success && commRes.data.Data) {
                    upfront = parseFloat(commRes.data.Data.total_upfront_fee) || 0;
                    placement = parseFloat(commRes.data.Data.total_placement_fee) || 0;
                    trail = parseFloat(commRes.data.Data.total_management_and_trail_fee) || 0;
                }
                setCommissionData({
                    upfront,
                    placement,
                    trail,
                    total: upfront + placement + trail
                });

                // 6. Top Distributors Mapping
                if (topDistRes.data.Success && Array.isArray(topDistRes.data.Data)) {
                    const mappedDistributors = topDistRes.data.Data.map(d => ({
                        name: d.distributor_name,
                        amount: `₹${parseFloat(d.total_commission).toLocaleString("en-IN")}`,
                        subtitle: "Monthly Performance",
                        status: "Committed",
                        statusColor: "#6366F1"
                    }));
                    setTopDistributors(mappedDistributors);
                }

                // 7. WIP Activity Mapping
                if (wipActivityRes.data.Success && Array.isArray(wipActivityRes.data.Data)) {
                    setWipActivityData(wipActivityRes.data.Data);
                }

                if (isMounted) setLastUpdated(formatTimestamp());
            } catch (error) {
                console.error("Error fetching dashboard KPIs:", error);
                if (isMounted) {
                    setKpiData({
                        distributors: { value: "N/A", subtitle: "Failed to load", trend: 0 },
                        kyc: { value: "N/A", subtitle: "Failed to load", trend: 0 }
                    });
                }
            }
        };

        fetchDashboardData();

        return () => {
            isMounted = false;
        };
    }, []);

    return (
        <div className="admin-overview">
            {/* Page Header */}
            <div className="admin-overview-header">
                <div>
                    <h1 className="admin-overview-title">Commercial Mapping</h1>
                    <p className="admin-overview-sub">Track your sales performance and commissions</p>
                </div>
                <span className="admin-overview-updated">
                    Last updated:&nbsp;<strong>{lastUpdated}</strong>
                </span>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                <KpiCard
                    title="Total Distributors"
                    value={kpiData.distributors.value}
                    subtitle={kpiData.distributors.subtitle}
                    trend={kpiData.distributors.trend}
                    icon={<TrendingUp size={20} />}
                    iconBgColor="#EEF2FF"
                    iconColor="#6366F1"
                />
                <KpiCard
                    title="KYC Pending"
                    value={kpiData.kyc.value}
                    subtitle={kpiData.kyc.subtitle}
                    trend={kpiData.kyc.trend}
                    icon={<UserMinus size={20} />}
                    iconBgColor="#FEF2F2"
                    iconColor="#EF4444"
                />
                <KpiCard
                    title="Invoices Summary"
                    value={invoiceStatus.pending}
                    subtitle={
                        <div className="flex flex-col gap-1 mt-1">
                            <div className="flex justify-between items-center text-[10px]">
                                <span className="text-gray-500">Created:</span>
                                <span className="font-bold text-gray-700">{invoiceStatus.draft}</span>
                            </div>
                            <div className="flex justify-between items-center text-[10px]">
                                <span className="text-gray-500">Pending:</span>
                                <span className="font-bold text-yellow-600">{invoiceStatus.pending}</span>
                            </div>
                            <div className="flex justify-between items-center text-[10px]">
                                <span className="text-gray-500">Payment Done:</span>
                                <span className="font-bold text-green-600">{invoiceStatus.approved}</span>
                            </div>
                        </div>
                    }
                    icon={<DollarSign size={20} />}
                    iconBgColor="#F5F3FF"
                    iconColor="#8B5CF6"
                />
            </div>

            {/* Charts Row 1: Sales Trend + Product Sales */}
            <div className="admin-charts-row">
                {/* Fund and Distributor Mapping */}
                <div className="dash-card admin-chart-main">
                    <div className="dash-card-header flex justify-between items-start">
                        <div>
                            <h3 className="dash-section-title">Fund and Distributor Mapping</h3>
                            <p className="dash-section-sub">Real-time overview of fund distribution across distributors</p>
                        </div>
                        <span className="text-[10px] text-gray-400 font-medium bg-gray-50 px-2 py-1 rounded-md border border-gray-100">
                            {monthInfo}
                        </span>
                    </div>
                    <div className="mt-4">
                        <ReusableTable
                            columns={[
                                { Header: "Distributor Name", accessor: "distributor_name" },
                                { Header: "Distributor PAN", accessor: "distributor_pan_number" },
                                { Header: "Fund Names", accessor: "fund_names" },
                            ]}
                            data={distributorFundDetails}
                        />
                    </div>
                </div>

                {/* Product Sales — Horizontal Bar */}
                <div className="dash-card admin-chart-side">
                    <div className="dash-card-header flex justify-between items-start">
                        <div>
                            <h3 className="dash-section-title">Product Sales</h3>
                            <p className="dash-section-sub">Distribution by product</p>
                        </div>
                        <span className="text-[10px] text-gray-400 font-medium bg-gray-50 px-2 py-1 rounded-md border border-gray-100">
                            {monthInfo}
                        </span>
                    </div>
                    {/* Custom horizontal bar with labels */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px", paddingTop: "8px" }}>
                        {productSalesData.length > 0 ? (
                            productSalesData.map((item, i) => {
                                const label = item.product_name && item.product_type_name && item.product_name !== item.product_type_name 
                                    ? `${item.product_name} ${item.product_type_name}` 
                                    : (item.product_name || item.product_type_name);
                                const value = parseInt(item.td_count || 0, 10);
                                const maxValue = Math.max(...productSalesData.map(d => parseInt(d.td_count || 0, 10)), 1);
                                const pct = (value / maxValue) * 100;
                                const colors = ["#6366F1", "#F59E0B", "#10B981", "#EF4444", "#3B82F6", "#8B5CF6", "#F97316"];
                                const color = colors[i % colors.length];

                                return (
                                    <div key={label + i}>
                                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                                            <span style={{ fontSize: "0.78rem", fontWeight: 600, color: "#374151" }}>{label}</span>
                                            <span style={{ fontSize: "0.78rem", color: "#6B7280" }}>{value} sales</span>
                                        </div>
                                        <div style={{ height: "8px", borderRadius: "8px", background: "#F3F4F6", overflow: "hidden" }}>
                                            <div
                                                style={{
                                                    height: "100%",
                                                    width: `${pct}%`,
                                                    backgroundColor: color,
                                                    borderRadius: "8px",
                                                    transition: "width 0.6s ease",
                                                }}
                                            />
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="text-sm text-gray-500 italic py-4 text-center">No sales products found this month</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Charts Row 2: Commission Breakdown + Top Distributors */}
            < div className="admin-charts-row" >
                {/* Commission Breakdown — Horizontal Bar */}
                < div className="dash-card admin-chart-main" >
                    <div className="dash-card-header flex justify-between items-start">
                        <div>
                            <h3 className="dash-section-title">Commission Breakdown</h3>
                            <p className="dash-section-sub">Total ₹{commissionData.total.toLocaleString("en-IN")} this month</p>
                        </div>
                        <span className="text-[10px] text-gray-400 font-medium bg-gray-50 px-2 py-1 rounded-md border border-gray-100">
                            {monthInfo}
                        </span>
                    </div>
                    <BarChart
                        labels={COMMISSION_DATA.labels}
                        datasets={[
                            {
                                label: "Amount",
                                data: [commissionData.upfront, commissionData.placement, commissionData.trail],
                                backgroundColor: ["#10B981", "#F59E0B", "#6366F1"],
                            }
                        ]}
                        height={200}
                        horizontal
                        showLegend={false}
                    />
                </div >

                {/* Top Distributors */}
                < div className="admin-chart-side" >
                    <TopDistributorsList distributors={topDistributors} monthInfo={monthInfo} />
                </div >
            </div >

            {/* WIP Distributor Last Activities */}
            <div className="dash-card">
                <div className="dash-card-header">
                    <h3 className="dash-section-title">Distributor Last Activities</h3>
                    <p className="dash-section-sub">Latest updates on work-in-progress distributors</p>
                </div>
                <div className="mt-4">
                    <ReusableTable
                        columns={[
                            { Header: "Distributor Name", accessor: "distributor_name" },
                            { Header: "Fund Name", accessor: "fund_name" },
                            { Header: "Class Code", accessor: "class_code" },
                            {
                                Header: "Agreement Status",
                                accessor: "agreement_status",
                                Cell: ({ value }) => (
                                    <span
                                        className="px-3 py-1 rounded-full text-[10px] font-bold"
                                        style={{
                                            backgroundColor: value === "Approved and Signed" ? "#D1FAE5" : "#FEF3C7",
                                            color: value === "Approved and Signed" ? "#065F46" : "#92400E"
                                        }}
                                    >
                                        {value}
                                    </span>
                                )
                            }
                        ]}
                        data={wipActivityData}
                    />
                </div>
            </div>
        </div >
    );
};

export default SalesOverviewDashboard;
